'use client';

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, Type, Hash, Clock, FileInput, X, BarChart2, BookOpen, Gauge, FileDown, AlertCircle, AlignLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textEditorContent } from '../../data/text-editor-content';
import Link from 'next/link';

type TextStats = {
    // Basic Counts
    characters: number;
    charactersNoSpaces: number;
    words: number;
    sentences: number;
    paragraphs: number;

    // Readability
    readingTime: number;
    speakingTime: number;
    readingEase: number;
    readingLevel: string;

    // Word Analysis
    avgWordLength: number;
    avgSentenceLength: number;
    avgParagraphLength: number;

    // Keyword Analysis
    keywordDensity: Array<{ word: string; count: number; density: number }>;

    // Text Structure
    shortSentences: number;
    longSentences: number;
    veryLongSentences: number;

    // Content Analysis
    uniqueWords: number;
    lexicalDiversity: number;
};

// Common English stop words
const STOP_WORDS = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'if', 'because', 'as', 'what', 'which',
    'this', 'that', 'these', 'those', 'then', 'just', 'so', 'than', 'such', 'both',
    'through', 'about', 'for', 'is', 'of', 'while', 'during', 'to', 'in', 'on',
    'at', 'by', 'from', 'up', 'down', 'under', 'over', 'again', 'further', 'then',
    'once', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both',
    'each', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not',
    'only', 'own', 'same', 'so', 'than', 'too', 'very', 'can', 'will', 'just',
    'don', 'should', 'now', 'd', 'll', 'm', 'o', 're', 've', 'y', 'ain', 'aren',
    'couldn', 'didn', 'doesn', 'hadn', 'hasn', 'haven', 'isn', 'ma', 'mightn',
    'mustn', 'needn', 'shan', 'shouldn', 'wasn', 'weren', 'won', 'wouldn'
]);

export default function TextStatistics() {
    const [text, setText] = useState('');
    const [copied, setCopied] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [wordsPerMinute, setWordsPerMinute] = useState(200);
    const [speechRate, setSpeechRate] = useState(150); // words per minute for speech
    const [filterStopWords, setFilterStopWords] = useState(true);
    const { toast } = useToast();

    const calculateStats = useCallback((text: string): TextStats => {
        if (!text.trim()) {
            return {
                characters: 0,
                charactersNoSpaces: 0,
                words: 0,
                sentences: 0,
                paragraphs: 0,
                readingTime: 0,
                speakingTime: 0,
                readingEase: 0,
                readingLevel: 'N/A',
                avgWordLength: 0,
                avgSentenceLength: 0,
                avgParagraphLength: 0,
                keywordDensity: [],
                shortSentences: 0,
                longSentences: 0,
                veryLongSentences: 0,
                uniqueWords: 0,
                lexicalDiversity: 0
            };
        }

        // Basic counts
        const characters = text.length;
        const charactersNoSpaces = text.replace(/\s+/g, '').length;

        // Words
        const words = text.trim() === '' ? [] : text.trim().split(/\s+/);
        const wordCount = words.length;

        // Sentences (split by .!? followed by space or end of string)
        const sentences = text.split(/[.!?]+\s*|\n\s*\n/).filter(s => s.trim().length > 0);
        const sentenceCount = sentences.length;

        // Paragraphs (split by double newline or more)
        const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
        const paragraphCount = paragraphs.length;

        // Reading and speaking time
        const readingTime = Math.ceil(wordCount / wordsPerMinute) || 1;
        const speakingTime = Math.ceil((wordCount / speechRate) * 60); // in seconds

        // Word analysis
        const totalWordLength = words.reduce((sum, word) => sum + word.length, 0);
        const avgWordLength = wordCount > 0 ? Number((totalWordLength / wordCount).toFixed(1)) : 0;

        // Sentence analysis
        const sentenceLengths = sentences.map(s => s.trim().split(/\s+/).length);
        const avgSentenceLength = sentenceCount > 0
            ? Number((sentenceLengths.reduce((a, b) => a + b, 0) / sentenceCount).toFixed(1))
            : 0;

        // Paragraph analysis
        const paragraphLengths = paragraphs.map(p => p.trim().split(/\s+/).length);
        const avgParagraphLength = paragraphCount > 0
            ? Number((paragraphLengths.reduce((a, b) => a + b, 0) / paragraphCount).toFixed(1))
            : 0;

        // Sentence complexity
        const shortSentences = sentenceLengths.filter(len => len < 10).length;
        const longSentences = sentenceLengths.filter(len => len >= 10 && len < 20).length;
        const veryLongSentences = sentenceLengths.filter(len => len >= 20).length;

        // Readability (Flesch-Kincaid Reading Ease)
        let readingEase = 0;
        let readingLevel = 'N/A';

        if (sentenceCount > 0 && wordCount > 0) {
            const avgWordsPerSentence = wordCount / sentenceCount;
            const syllables = words.reduce((count, word) => count + countSyllables(word), 0);
            const avgSyllablesPerWord = syllables / wordCount;

            // Flesch-Kincaid Reading Ease
            readingEase = Math.max(0, Math.min(100,
                206.835 - (1.015 * avgWordsPerSentence) - (84.6 * avgSyllablesPerWord)
            ));

            // Determine reading level
            if (readingEase >= 90) readingLevel = '5th grade';
            else if (readingEase >= 80) readingLevel = '6th grade';
            else if (readingEase >= 70) readingLevel = '7th grade';
            else if (readingEase >= 60) readingLevel = '8th-9th grade';
            else if (readingEase >= 50) readingLevel = '10th-12th grade';
            else if (readingEase >= 30) readingLevel = 'College';
            else readingLevel = 'College Graduate';
        }

        // Keyword density (top 10)
        const wordFreq: Record<string, number> = {};
        words.forEach(word => {
            const cleanWord = word.toLowerCase().replace(/[^\w]/g, '');
            if (cleanWord.length >= 3 && (!filterStopWords || !STOP_WORDS.has(cleanWord))) {
                wordFreq[cleanWord] = (wordFreq[cleanWord] || 0) + 1;
            }
        });

        const keywordDensity = Object.entries(wordFreq)
            .map(([word, count]) => ({
                word,
                count,
                density: Number(((count / wordCount) * 100).toFixed(2))
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        // Lexical diversity
        const uniqueWords = new Set(words.map(w => w.toLowerCase()));
        const lexicalDiversity = wordCount > 0
            ? Number(((uniqueWords.size / wordCount) * 100).toFixed(1))
            : 0;

        return {
            characters,
            charactersNoSpaces,
            words: wordCount,
            sentences: sentenceCount,
            paragraphs: paragraphCount,
            readingTime,
            speakingTime,
            readingEase,
            readingLevel,
            avgWordLength,
            avgSentenceLength,
            avgParagraphLength,
            keywordDensity,
            shortSentences,
            longSentences,
            veryLongSentences,
            uniqueWords: uniqueWords.size,
            lexicalDiversity
        };
    }, [wordsPerMinute, speechRate, filterStopWords]);

    // Helper function to count syllables in a word
    const countSyllables = (word: string): number => {
        word = word.toLowerCase();
        if (word.length <= 3) return 1;

        // Remove final 'es', 'ed', 'e' not preceeded by a vowel
        word = word.replace(/(?:[^laeiouy]|ed|[^laeiouy]e)$/, '');
        word = word.replace(/^y/, '');

        // Count vowel groups
        const syllableCount = (word.match(/[aeiouy]{1,2}/g) || []).length;
        return Math.max(1, syllableCount);
    };

    const stats = useMemo(() => calculateStats(text), [text, calculateStats]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setText(fileContent);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => {
        setText('');
    };

    const exportStats = () => {
        const statsText = `Text Statistics Report
=====================

Basic Counts:
- Characters: ${stats.characters.toLocaleString()}
- Characters (no spaces): ${stats.charactersNoSpaces.toLocaleString()}
- Words: ${stats.words.toLocaleString()}
- Sentences: ${stats.sentences.toLocaleString()}
- Paragraphs: ${stats.paragraphs.toLocaleString()}
- Unique words: ${stats.uniqueWords.toLocaleString()}
- Lexical diversity: ${stats.lexicalDiversity}%

Reading Analysis:
- Reading time: ~${stats.readingTime} minute${stats.readingTime !== 1 ? 's' : ''} (${wordsPerMinute} wpm)
- Speaking time: ~${Math.floor(stats.speakingTime / 60)}m ${stats.speakingTime % 60}s
- Reading ease: ${stats.readingEase.toFixed(1)} (${stats.readingLevel} level)

Averages:
- Word length: ${stats.avgWordLength} characters
- Sentence length: ${stats.avgSentenceLength} words
- Paragraph length: ${stats.avgParagraphLength} words

Sentence Structure:
- Short (<10 words): ${stats.shortSentences} (${stats.sentences ? Math.round((stats.shortSentences / stats.sentences) * 100) : 0}%)
- Long (10-19 words): ${stats.longSentences} (${stats.sentences ? Math.round((stats.longSentences / stats.sentences) * 100) : 0}%)
- Very long (20+ words): ${stats.veryLongSentences} (${stats.sentences ? Math.round((stats.veryLongSentences / stats.sentences) * 100) : 0}%)

Top Keywords:
${stats.keywordDensity.map(kw => `- ${kw.word}: ${kw.count} (${kw.density}%)`).join('\n')}
`;

        const blob = new Blob([statsText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'text-stats-report.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Text Stats">
                Use this tool for text stats. Free, no sign-up. Instant results in your browser.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>Text Input</CardTitle>
                                    <CardDescription>Enter or paste your text to analyze</CardDescription>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="text-xs"
                                    >
                                        {showAdvanced ? 'Hide Options' : 'Show Options'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={text}
                                    onChange={handleTextChange}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[300px] w-full text-base"
                                    rows={10}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <FileInput className="h-4 w-4 mr-2" />
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx,.pdf"
                                            />
                                        </Label>
                                    </Button>
                                </div>

                                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                                    <span>{stats.words.toLocaleString()} words</span>
                                    <span>•</span>
                                    <span>{stats.characters.toLocaleString()} chars</span>
                                    <span>•</span>
                                    <span>{stats.sentences.toLocaleString()} sentences</span>
                                </div>
                            </div>

                            {showAdvanced && (
                                <div className="pt-4 border-t mt-4 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="words-per-minute">
                                                Reading speed: {wordsPerMinute} wpm
                                            </Label>
                                            <Slider
                                                id="words-per-minute"
                                                value={[wordsPerMinute]}
                                                onValueChange={([value]) => setWordsPerMinute(value)}
                                                min={100}
                                                max={400}
                                                step={25}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="speech-rate">
                                                Speaking rate: {speechRate} wpm
                                            </Label>
                                            <Slider
                                                id="speech-rate"
                                                value={[speechRate]}
                                                onValueChange={([value]) => setSpeechRate(value)}
                                                min={100}
                                                max={200}
                                                step={10}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="filter-stop-words"
                                            checked={filterStopWords}
                                            onCheckedChange={setFilterStopWords}
                                        />
                                        <Label htmlFor="filter-stop-words">Filter common stop words in keyword analysis</Label>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Text Statistics</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <StatCard
                                    icon={<Type className="h-4 w-4" />}
                                    label="Words"
                                    value={stats.words.toLocaleString()}
                                />
                                <StatCard
                                    icon={<Hash className="h-4 w-4" />}
                                    label="Characters"
                                    value={stats.characters.toLocaleString()}
                                />
                                <StatCard
                                    icon={<FileText className="h-4 w-4" />}
                                    label="Sentences"
                                    value={stats.sentences.toLocaleString()}
                                />
                                <StatCard
                                    icon={<AlignLeft className="h-4 w-4" />}
                                    label="Paragraphs"
                                    value={stats.paragraphs.toLocaleString()}
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Reading Time</span>
                                    <span className="text-sm font-medium">
                                        ~{stats.readingTime} min ({wordsPerMinute} wpm)
                                    </span>
                                </div>
                                <Progress
                                    value={Math.min(100, stats.readingTime * 10)}
                                    className="h-2"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Reading Ease</span>
                                    <span className="text-sm font-medium">{stats.readingEase.toFixed(1)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                    <span>Difficult</span>
                                    <span>Easy</span>
                                </div>
                                <Progress
                                    value={stats.readingEase}
                                    className="h-2"
                                    indicatorClassName="bg-green-500"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    {stats.readingLevel} level
                                </p>
                            </div>

                            <div className="space-y-2">
                                <h3 className="text-sm font-medium">Sentence Length</h3>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">Short (&lt;10 words)</span>
                                        <span>{stats.shortSentences} ({stats.sentences ? Math.round((stats.shortSentences / stats.sentences) * 100) : 0}%)</span>
                                    </div>
                                    <Progress
                                        value={stats.sentences ? (stats.shortSentences / stats.sentences) * 100 : 0}
                                        className="h-1.5"
                                        indicatorClassName="bg-blue-500"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">Medium (10-19 words)</span>
                                        <span>{stats.longSentences} ({stats.sentences ? Math.round((stats.longSentences / stats.sentences) * 100) : 0}%)</span>
                                    </div>
                                    <Progress
                                        value={stats.sentences ? (stats.longSentences / stats.sentences) * 100 : 0}
                                        className="h-1.5"
                                        indicatorClassName="bg-green-500"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">Long (20+ words)</span>
                                        <span>{stats.veryLongSentences} ({stats.sentences ? Math.round((stats.veryLongSentences / stats.sentences) * 100) : 0}%)</span>
                                    </div>
                                    <Progress
                                        value={stats.sentences ? (stats.veryLongSentences / stats.sentences) * 100 : 0}
                                        className="h-1.5"
                                        indicatorClassName="bg-yellow-500"
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={exportStats}
                                    disabled={!text}
                                >
                                    <FileDown className="h-4 w-4 mr-2" />
                                    Export Report
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Top Keywords</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats.keywordDensity.length > 0 ? (
                                <div className="space-y-3">
                                    {stats.keywordDensity.map((kw, i) => (
                                        <div key={i} className="space-y-1">
                                            <div className="flex justify-between text-sm">
                                                <span className="font-medium">{kw.word}</span>
                                                <span className="text-muted-foreground">{kw.count} ({kw.density}%)</span>
                                            </div>
                                            <Progress
                                                value={kw.density * 2}
                                                className="h-1.5"
                                                indicatorClassName="bg-purple-500"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted-foreground text-sm">
                                    {text ? 'No significant keywords found' : 'Enter text to see keyword analysis'}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Text Statistics</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground flex items-center">
                                    <BookOpen className="h-4 w-4 mr-2" />
                                    Reading Ease
                                </h3>
                                <ul className="space-y-1">
                                    <li className="flex items-start">
                                        <span className="text-green-500 mr-2">90-100</span>
                                        <span>Very easy to read</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-blue-500 mr-2">80-89</span>
                                        <span>Easy to read</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-yellow-500 mr-2">60-79</span>
                                        <span>Plain English</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-orange-500 mr-2">30-59</span>
                                        <span>Difficult to read</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-red-500 mr-2">0-29</span>
                                        <span>Very difficult to read</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-2" />
                                    Tips for Better Readability
                                </h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Aim for an average sentence length of 15-20 words</li>
                                    <li>Keep paragraphs under 6 sentences</li>
                                    <li>Use active voice when possible</li>
                                    <li>Vary sentence structure and length</li>
                                    <li>Use bullet points for lists</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={textEditorContent.title}
                    description={textEditorContent.description}
                    content={textEditorContent.content}
                    faqs={textEditorContent.faqs}
                    relatedTools={textEditorContent.relatedTools}
                />
            </div>
        </div>
    );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
    return (
        <div className="border rounded-lg p-3">
            <div className="flex items-center space-x-2 text-muted-foreground">
                {icon}
                <span className="text-xs">{label}</span>
            </div>
            <p className="mt-1 text-xl font-semibold">{value}</p>
        </div>
    );
}
